import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Layout } from '@/shell/Layout'
import { SectionLabel } from '@/shared/components/SectionLabel'
import { useAccessToken } from '@/lib/hooks'
import { useSelectedOrg } from '@/context/SelectedOrgContext'
import { s } from '@/features/integrations/guideStyles'
import { GUIDES, GUIDE_LABEL, CATEGORIES, type GuideId } from '@/features/integrations/guides'
import { CliGettingStartedGuide } from '@/features/integrations/guides/CliGettingStartedGuide'
import { CliAuthGuide } from '@/features/integrations/guides/CliAuthGuide'
import { CliCommandsGuide } from '@/features/integrations/guides/CliCommandsGuide'
import { CliCiGuide } from '@/features/integrations/guides/CliCiGuide'
import { StoringStateGuide } from '@/features/integrations/guides/StoringStateGuide'
import { ModulePackagingGuide } from '@/features/integrations/guides/ModulePackagingGuide'
import { ModulePublishingGuide } from '@/features/integrations/guides/ModulePublishingGuide'
import { ModuleConsumptionGuide } from '@/features/integrations/guides/ModuleConsumptionGuide'
import { UsingProvidersGuide } from '@/features/integrations/guides/UsingProvidersGuide'
import { LockFilesGuide } from '@/features/integrations/guides/LockFilesGuide'
import { PassiveDriftGuide } from '@/features/integrations/guides/PassiveDriftGuide'
import { OidcTrustGuide } from '@/features/integrations/guides/OidcTrustGuide'
import { TroubleshootingGuide } from '@/features/integrations/guides/TroubleshootingGuide'
import { PolicyGuide } from '@/features/integrations/guides/PolicyGuide'

export function Integrations() {
  const navigate = useNavigate()
  const { guide: guideParam } = useParams<{ guide?: string }>()
  // The CLI is the preferred path, so a bare /integrations lands on its guide.
  const guide: GuideId = GUIDES.some((g) => g.id === guideParam)
    ? (guideParam as GuideId)
    : 'cli-getting-started'
  const selectGuide = (id: GuideId) => navigate(`/integrations/${id}`)

  const { token } = useAccessToken()
  const { selectedOrg } = useSelectedOrg()

  // The page scrolls inside Layout's .app-content div, not the window, so a
  // guide switch keeps the old scroll position without this.
  useEffect(() => {
    document.querySelector('.app-content')?.scrollTo({ top: 0 })
  }, [guide])

  return (
    <Layout title="Guides & Integrations">
      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>
        {/* Left sub-nav — grouped by category */}
        <div style={s.sidebar}>
          {CATEGORIES.map((cat) => (
            <div key={cat.label} style={s.navGroup}>
              <div style={s.navGroupLabel}>
                <SectionLabel>// {cat.label}</SectionLabel>
              </div>
              {cat.guides.map((id) => (
                <button
                  key={id}
                  onClick={() => selectGuide(id)}
                  aria-current={guide === id ? 'page' : undefined}
                  style={{ ...s.navItem, ...s.navSubItem, ...(guide === id ? s.navItemActive : {}) }}
                >
                  {GUIDE_LABEL[id]}
                </button>
              ))}
              {cat.soon?.map((label) => (
                <button
                  key={label}
                  disabled
                  style={{ ...s.navItem, ...s.navSubItem, opacity: 0.55, cursor: 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  {label}
                  <span className="badge badge-neutral" style={{ fontSize: '0.52rem' }}>soon</span>
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Guide content */}
        <div style={s.content}>
          {guide === 'cli-getting-started' && <CliGettingStartedGuide org={selectedOrg} onSwitchGuide={selectGuide} />}
          {guide === 'cli-auth'          && <CliAuthGuide          org={selectedOrg} onSwitchGuide={selectGuide} />}
          {guide === 'cli-commands'      && <CliCommandsGuide      onSwitchGuide={selectGuide} />}
          {guide === 'cli-ci'            && <CliCiGuide            org={selectedOrg} onSwitchGuide={selectGuide} />}
          {guide === 'storing-state'     && <StoringStateGuide     org={selectedOrg} token={token} onSwitchGuide={selectGuide} />}
          {guide === 'module-packaging'  && <ModulePackagingGuide  onSwitchGuide={selectGuide} />}
          {guide === 'module-publishing' && <ModulePublishingGuide org={selectedOrg} token={token} onSwitchGuide={selectGuide} />}
          {guide === 'module-consumption' && <ModuleConsumptionGuide org={selectedOrg} token={token} onSwitchGuide={selectGuide} />}
          {guide === 'using-providers'   && <UsingProvidersGuide   org={selectedOrg} token={token} onSwitchGuide={selectGuide} />}
          {guide === 'lock-files'        && <LockFilesGuide        org={selectedOrg} token={token} onSwitchGuide={selectGuide} />}
          {guide === 'passive-drift'     && <PassiveDriftGuide     org={selectedOrg} token={token} onSwitchGuide={selectGuide} />}
          {guide === 'oidc-trust'        && <OidcTrustGuide        org={selectedOrg} onSwitchGuide={selectGuide} />}
          {guide === 'policy-modules'    && <PolicyGuide artefact="module" />}
          {guide === 'policy-state'      && <PolicyGuide artefact="state" />}
          {guide === 'policy-providers'  && <PolicyGuide artefact="provider" />}
          {guide === 'troubleshooting'   && <TroubleshootingGuide />}
        </div>
      </div>
    </Layout>
  )
}
